import { DocumentNode } from "graphql"
import { useQuery, QueryHookOptions } from "react-apollo"
import { OperationVariables } from "@apollo/react-common"

import { mapApolloError } from "shared/apollo/helpers"
import { ReadyQuery, LoadingQuery, ErroredQuery } from "typings/smart-query"

/**
 * A thin wrapper around react-apollo hooks to support most of our use cases and avoid a lot of null/error checks
 * @returns {ReadyQuery | LoadingQuery | ErroredQuery}
 */
export function useSmartQuery<TData, TVariables = OperationVariables>(
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables>
): ReadyQuery<TData, TVariables> | LoadingQuery<TData> | ErroredQuery<TData, TVariables> {
  const { loading, error, data, ...rest } = useQuery(query, options)

  if (loading) {
    return {
      loading: true,
      data: undefined,
      error: undefined,
      ...rest
    }
  }

  if (error) {
    return {
      loading: false,
      data: undefined,
      error: mapApolloError(error),
      ...rest
    }
  }

  if (!data) {
    throw new Error("Unexpected: data is empty, but we are not loading and there is no error.")
  }

  return {
    loading: false,
    error: undefined,
    data,
    ...rest
  }
}

/**
 * Instead of returning an error potentially assert that it won't happen and
 * throw if it does. This will push it to our error boundary.
 *
 * @throws ApolloError when an error is returned
 * @returns {LoadingQuery | ReadyQuery}
 */
export function useAssertSmartQuery<TData, TVariables = OperationVariables>(
  query: DocumentNode,
  options?: QueryHookOptions<TData, TVariables>
): ReadyQuery<TData, TVariables> | LoadingQuery<TData> {
  const result = useSmartQuery(query, options)

  if (result.error) throw result.error

  return result
}
